import type { ReactNode } from 'react'
import { assertNever } from '../../theme/statusTone'

// Notice — ATC-128.
//
// Emits the EXISTING `.notice` / `.notice-ok` / `.notice-warn` / `.notice-error`
// class names, so it adds zero CSS and carries zero visual risk. What it adds is
// `role` and `aria-live`, which none of the twelve hand-written notice divs in
// this console have -- an inline success or failure message that appears after a
// write action is currently announced to nobody.
//
// WHEN TO USE THIS AND WHEN NOT TO (ATC-136 migrated the console to this rule).
//
// USE IT for a notice whose appearance IS the event — a failed write, a
// confirmation, a load error. That is what a live region is for, and it is the
// entire value this primitive adds.
//
// DO NOT use it for STATIC copy rendered on open, which is most of the impact
// text inside a confirm dialog. `tone="error"` carries `role="alert"`, which
// most screen readers announce on mount, so it would interrupt the dialog's own
// title. Those stay as plain `.notice` divs, and each site says so.
//
// One further exception, in `main.tsx`: the config-bootstrap failure renders
// before the app tree these primitives live in exists, so importing a component
// there would put a second failure mode inside the handler for the first.

export type NoticeTone = 'info' | 'ok' | 'warn' | 'error'

function toneClass(tone: NoticeTone): string {
  switch (tone) {
    case 'info':
      return 'notice'
    case 'ok':
      return 'notice notice-ok'
    case 'warn':
      return 'notice notice-warn'
    case 'error':
      return 'notice notice-error'
    default:
      // The FUNCTION form, deliberately. `tsconfig.json` sets
      // `noUnusedLocals: true`, which makes the idiomatic
      // `const _exhaustive: never = tone` a BUILD FAILURE rather than the
      // compile-time guard it is meant to be.
      return assertNever(tone)
  }
}

export interface NoticeProps {
  tone?: NoticeTone
  children: ReactNode
}

export function Notice({ tone = 'info', children }: NoticeProps) {
  // `alert` is assertive and interrupts; `status` is polite. An error after a
  // failed write is worth interrupting for, a success confirmation is not.
  const isError = tone === 'error'
  return (
    <div
      className={toneClass(tone)}
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
    >
      {children}
    </div>
  )
}
